import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink } from "react-router-dom";
import { X, Phone, Mail, ArrowUpRight } from "lucide-react";
import { getLenis } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

export function MobileMenu({ open, onClose, links, contact, logoUrl }) {
  useEffect(() => {
    const lenis = getLenis();
    if (open) {
      lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenis?.start();
      document.body.style.overflow = "";
    }
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            data-testid="mobile-menu-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm sm:hidden"
          />
          <motion.aside
            key="panel"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: EASE }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-[85vw] max-w-sm bg-navy text-white flex flex-col sm:hidden"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
              <img
                src={logoUrl}
                alt="Drywall Local 675"
                className="h-12 w-auto"
                data-testid="mobile-menu-logo"
              />
              <button
                onClick={onClose}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="grid place-items-center h-11 w-11 -mr-2 text-white/80 hover:text-white transition-colors duration-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <nav
              className="flex-1 px-6 pt-10 flex flex-col"
              data-testid="mobile-nav"
            >
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.15 + i * 0.08,
                    ease: EASE,
                  }}
                >
                  <NavLink
                    to={l.to}
                    onClick={onClose}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `group flex items-center justify-between py-5 border-b border-white/10 font-heading font-bold uppercase text-3xl tracking-tight transition-colors duration-300 ${
                        isActive
                          ? "text-brand-sky"
                          : "text-white hover:text-brand-sky"
                      }`
                    }
                  >
                    {l.label}
                    <ArrowUpRight className="h-6 w-6 opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45, ease: EASE }}
              className="px-6 pb-10 pt-6 border-t border-white/10"
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-bold mb-4">
                Reach the office
              </p>
              <a
                href={`tel:${contact.phone.replace(/\D/g, "")}`}
                data-testid="mobile-menu-phone"
                className="flex items-center gap-3 py-2 text-sm font-semibold text-white/85 hover:text-brand-sky transition-colors duration-300"
              >
                <Phone className="h-4 w-4 text-brand-sky" /> {contact.phone}
              </a>
              <a
                href={`mailto:${contact.email}`}
                data-testid="mobile-menu-email"
                className="flex items-center gap-3 py-2 text-sm font-semibold text-white/85 hover:text-brand-sky transition-colors duration-300 break-all"
              >
                <Mail className="h-4 w-4 text-brand-sky shrink-0" />{" "}
                {contact.email}
              </a>
            </motion.div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
